import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Spinner } from '../components/ui';
import { useSession } from '../features/auth/useSession';

function Loading() {
  return (
    <div className="flex min-h-full items-center justify-center bg-neutral-50">
      <Spinner />
    </div>
  );
}

/** Gate for authenticated app routes: redirect to /login when signed out. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isLoading } = useSession();

  if (isLoading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  // Platform admins live in /admin, not the workspace app.
  if (user.isSuperAdmin) return <Navigate to="/admin" replace />;
  return <>{children}</>;
}

/** Gate for the platform admin area: super admins only. */
export function RequireSuperAdmin({ children }: { children: ReactNode }) {
  const { user, isLoading } = useSession();

  if (isLoading) return <Loading />;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!user.isSuperAdmin) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

/** Inverse gate: keep authenticated users out of /login and /register. */
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { user, isLoading } = useSession();
  if (isLoading) return null;
  if (user) return <Navigate to={user.isSuperAdmin ? '/admin' : '/app'} replace />;
  return <>{children}</>;
}
